import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import { BEHAVIOUR_OPTIONS } from '../../../common/constants';
import CommonSelect from '../../../components/CommonSelect';
import { USERS_LISTING } from './graphql/Queries';

function IncidentLogModal({ open, loading, onFinish, onCancel, form }) {
  const [userSearch, setUserSearch] = useState('');
  const [submittable, setSubmittable] = useState(false);
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, formValues]);

  const handleUserSearch = debounce((value) => {
    setUserSearch(value);
  }, 500);

  return (
    <Modal
      title="Log Incident"
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      afterClose={() => {
        form.resetFields();
        setUserSearch('');
        setSubmittable(false);
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ count: 1, behaviour: 'standup' }}
      >
        <Form.Item
          name="employeeId"
          label="Employee"
          rules={[{ required: true, message: 'Please select an employee' }]}
        >
          <CommonSelect
            placeholder="Search employee"
            showSearch
            allowClear
            query={USERS_LISTING}
            responsePath="usersAdminAttendanceLog.users"
            valuePath="id"
            getLabel={(item) => `${item.firstName} ${item.lastName}`}
            conditionToCheckBeforeQuery={open}
            onSearch={handleUserSearch}
            variables={{
              filters: { searchStrings: userSearch ? [userSearch] : [] },
            }}
            useEffectDeps={[userSearch, open]}
          />
        </Form.Item>

        <Form.Item
          name="date"
          label="Date"
          rules={[{ required: true, message: 'Please select a date' }]}
          initialValue={dayjs()}
        >
          <DatePicker
            showTime
            style={{ width: '100%' }}
            placeholder="Select date & time"
            format="DD MMM YYYY, hh:mm A"
            disabledDate={(current) =>
              current && current > dayjs().endOf('day')
            }
            disabledTime={(current) => {
              if (!current || !current.isSame(dayjs(), 'day')) {
                return {};
              }
              const now = dayjs();
              return {
                disabledHours: () =>
                  Array.from({ length: 24 }, (_, hour) => hour).filter(
                    (hour) => hour > now.hour(),
                  ),
                disabledMinutes: (selectedHour) =>
                  selectedHour === now.hour()
                    ? Array.from({ length: 60 }, (_, minute) => minute).filter(
                        (minute) => minute > now.minute(),
                      )
                    : [],
              };
            }}
          />
        </Form.Item>

        <Form.Item
          name="behaviour"
          label="Behaviour"
          rules={[{ required: true, message: 'Please select a behaviour' }]}
        >
          <Select
            placeholder="Select behaviour"
            options={BEHAVIOUR_OPTIONS.map((b) => ({
              label: b.label,
              value: b.value,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="count"
          label="Count"
          rules={[
            { required: true, message: 'Please enter a count' },
            {
              type: 'number',
              max: 100,
              message: 'Count cannot exceed 100',
            },
          ]}
        >
          <InputNumber
            min={1}
            style={{ width: '100%' }}
            placeholder="Enter count"
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Reason"
          rules={[{ max: 500, message: 'Reason cannot exceed 500 characters' }]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Enter reason (optional)"
          />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!submittable}
            >
              Log Incident
            </Button>
            <Button onClick={onCancel}>Cancel</Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

export default IncidentLogModal;
